<template>
  <div
    v-if="lastPage !== 1"
    class="mt-[55px] flex justify-center md:mt-[123px]"
  >
    <slot
      v-if="hasPreviousPage"
      v-bind="{ canNavigateLeft, previousPage }"
      name="previous-button"
    >
      <PaginationButton
        :disabled="!canNavigateLeft"
        :page="previousPage"
        :aria-label="$t('navigation.pagination.previous')"
      >
        <IconPaginationArrowLeft />
      </PaginationButton>
    </slot>

    <slot
      name="first-page-button"
      v-bind="{ canNavigateLeft, previousPage, showFirst }"
    >
      <PaginationButton
        v-if="showFirst"
        :page="firstPage"
        :aria-label="$t('navigation.pagination.page', { page: firstPage })"
      >
        {{ firstPage }}
      </PaginationButton>
    </slot>

    <slot v-if="showFirstDots" name="page-dots">
      <div class="px-0 py-3 md:px-3">...</div>
    </slot>

    <slot name="page-buttons" v-bind="{ limitedPageNumbers, currentPage }">
      <PaginationButton
        v-for="page in limitedPageNumbers"
        :key="page"
        :is-active="currentPage === page"
        :page="page"
        :aria-label="$t('navigation.pagination.page', { page })"
      >
        {{ page }}
      </PaginationButton>
    </slot>

    <slot v-if="showLastDots" name="page-dots">
      <div class="px-0 py-3 md:px-3">...</div>
    </slot>

    <slot name="last-page-button" v-bind="{ lastPage, showLast }">
      <PaginationButton
        v-if="showLast"
        :page="lastPage"
        :aria-label="$t('navigation.pagination.page', { page: lastPage })"
      >
        {{ lastPage }}
      </PaginationButton>
    </slot>

    <slot
      v-if="hasNextPage"
      name="next-button"
      v-bind="{ canNavigateRight, nextPage }"
    >
      <PaginationButton
        :disabled="!canNavigateRight"
        :page="nextPage"
        :aria-label="$t('navigation.pagination.next')"
      >
        <IconPaginationArrowRight />
      </PaginationButton>
    </slot>
  </div>
</template>

<script lang="ts">
import { toRefs, defineComponent } from 'vue'
import usePagination from '#layers/base/composables/usePagination'
import PaginationButton from '~/components/ui/pagination/PaginationButton.vue'

export default defineComponent({
  name: 'Pagination',
  components: {
    PaginationButton,
  },
  props: {
    firstPage: {
      type: Number,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    lastPage: {
      type: Number,
      required: true,
    },
    visible: {
      type: Number,
      default: 5,
    },
  },
  setup(props) {
    const { visible, firstPage, currentPage, lastPage } = toRefs(props)

    const pagination = usePagination({
      firstPage,
      currentPage,
      lastPage,
      visiblePages: visible,
    })

    return { ...pagination }
  },
})
</script>
